import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import HeroSlider from '../components/HeroSlider';

const { FiBookOpen, FiUsers, FiAward, FiClock, FiCheckCircle, FiArrowRight, FiStar, FiTrendingUp, FiBrain, FiTarget, FiShield } = FiIcons;

const Homepage = () => {
  const services = [
    {
      icon: FiBookOpen,
      title: 'Assignment Support',
      description: 'Expert help with essays, research papers, and dissertations'
    },
    {
      icon: FiUsers,
      title: 'Tutoring Services',
      description: 'One-to-one and group sessions across all subjects'
    },
    {
      icon: FiAward,
      title: 'Skill Development',
      description: 'Build academic writing and research skills'
    },
    {
      icon: FiClock,
      title: '24/7 Support',
      description: 'Available whenever you need academic assistance'
    }
  ];

  const benefits = [
    'Improved Understanding',
    'Time & Workload Management',
    'Skill Development',
    'Better Grades',
    'Confidence Boost'
  ];

  const detailedBenefits = [
    {
      icon: FiBrain,
      title: 'Improved Understanding',
      description: 'Learn complex concepts with clear, simple explanations.'
    },
    {
      icon: FiClock,
      title: 'Time & Workload Management',
      description: 'Balance study, part-time jobs, and personal life.'
    },
    {
      icon: FiAward,
      title: 'Skill Development',
      description: 'Master academic writing, research, and citation skills.'
    },
    {
      icon: FiTrendingUp,
      title: 'Better Grades',
      description: 'Quality work and expert guidance to help you achieve top results.'
    },
    {
      icon: FiTarget,
      title: 'Confidence Boost',
      description: 'Build long-term knowledge and problem-solving ability.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Submit Your Tutorial',
      description: 'Share your tutorial needs so we can understand your requirements.'
    },
    {
      number: '2',
      title: 'Get Matched with an Expert',
      description: 'We connect you with the most suitable tutor or subject expert.'
    },
    {
      number: '3',
      title: 'Receive Guidance',
      description: 'Learn step-by-step methods, concepts, and tips to complete your assignment with confidence.'
    }
  ];

  const testimonials = [
    {
      text: "I was struggling with my dissertation, but Tutor Hub Online's expert guidance not only helped me finish on time but also improved my writing skills.",
      author: "Hannah",
      rating: 5
    },
    {
      text: "The tutoring sessions were amazing. My grades improved, and I finally understood statistics!",
      author: "Daniel",
      rating: 5
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <div>
      {/* Hero Slider */}
      <HeroSlider />

      {/* Services Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-16 bg-muted"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Key Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive academic support designed to help you succeed in every aspect of your educational journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">{service.title}</h3>
                <p className="text-gray-600 text-sm text-center">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* About Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants} className="relative">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                src="https://i.ibb.co/NnVpBZNq/About-Tutor-Hub-Online.jpg"
                alt="About Tutor Hub Online"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">Welcome to Tutor Hub Online</h2>
              <p className="text-gray-600 text-lg">
                Your trusted partner in academic success. We specialize in providing one-on-one tutoring services tailored specifically for higher education students. Our mission is to guide you through your coursework and assignments, ensuring you achieve your academic goals with confidence.
              </p>
              <p className="text-gray-600">
                At Tutor Hub Online, we understand that every student has unique needs and challenges. That's why our team of online tutors comprises experts from various fields, ready to support you in your studies. Whether you're pursuing a degree in Business Studies, Psychology, Computer Science, Engineering, Medicine, or Law, we have the knowledge and experience to assist you.
              </p>
              <p className="text-gray-600">
                In addition to these core subjects, we also offer tutoring in popular areas such as the Biological and Health Sciences, Sociology, and Design. Our tutors are well-versed in the intricacies of Bachelor of Arts (BA) and Bachelor of Science (BSc) programs, ensuring that you receive comprehensive support tailored to your curriculum.
              </p>
              <p className="text-gray-600">
                We are committed to fostering a positive learning environment that encourages curiosity and growth. Join us at Tutor Hub Online and take the next step in achieving 100% pass results in your chosen subjects.
              </p>

              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex items-center space-x-3"
                  >
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div whileHover={{ x: 10 }} transition={{ duration: 0.2 }}>
                <Link
                  to="/about"
                  className="inline-flex items-center text-primary font-semibold hover:text-blue-700 transition-colors"
                >
                  Learn More About Us
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Benefits Section - Updated Image */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-16 bg-gray-900 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Benefits of Tutor Hub Online</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Discover how our comprehensive academic support transforms your learning experience and academic performance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Benefits List - Left Side */}
            <motion.div variants={itemVariants} className="space-y-4">
              {detailedBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ x: 10 }}
                  className="flex items-start space-x-3 p-3 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <SafeIcon icon={benefit.icon} className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{benefit.title}</h3>
                    <p className="text-gray-300 text-sm">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}

              <motion.div variants={itemVariants} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="pt-4">
                <Link
                  to="/book-tutor"
                  className="inline-flex items-center bg-accent text-black px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  👉 Experience These Benefits
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </Link>
              </motion.div>
            </motion.div>

            {/* Updated Image - Right Side */}
            <motion.div variants={itemVariants} className="w-full">
              <div className="w-full h-auto">
                <motion.img
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  src="https://i.ibb.co/RGdjDPSz/Benefits-of-Tutor-Hub-Online.png"
                  alt="Benefits of Tutor Hub Online"
                  className="w-full h-auto min-h-[300px] max-h-[600px] object-cover object-center rounded-2xl shadow-2xl"
                />
                <div className="relative -mt-16 mb-4 ml-6">
                  <div className="inline-block bg-black/70 backdrop-blur-sm text-white p-3 rounded-xl">
                    <div className="text-xl font-bold text-accent">95%</div>
                    <div className="text-xs">Student Success Rate</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Core Services */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20 bg-muted"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Core Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive academic support tailored to your specific needs and academic goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-3xl">📘</span>
                <h3 className="text-xl font-semibold text-gray-900">Assignment Support</h3>
              </div>
              <div className="mb-6">
                <img 
                  src="https://i.ibb.co/nsP2kqmh/Assignment-Support.jpg" 
                  alt="Assignment Support Services" 
                  className="w-full h-48 object-cover rounded-lg" 
                />
              </div>
              <p className="text-gray-600 mb-4">
                Struggling with essays, research papers, case studies, or dissertations? Our academic experts assist you with proofreading, plagiarism checks, updates, and corrections:
              </p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• Well-researched, assignments checked for plagiarism and accuracy</li>
                <li>• Structured writing with correct referencing (APA, MLA, Harvard, etc.)</li>
                <li>• Step-by-step solutions to help you learn while you score</li>
              </ul>
              <Link
                to="/assignment-support"
                className="inline-flex items-center text-primary font-semibold hover:text-blue-700 transition-colors group"
              >
                Learn More
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-3xl">🎓</span>
                <h3 className="text-xl font-semibold text-gray-900">Tutoring Services</h3>
              </div>
              <div className="mb-6">
                <img 
                  src="https://i.ibb.co/W4cg0pRY/Tutoring-Services.jpg" 
                  alt="Tutoring Services" 
                  className="w-full h-48 object-cover rounded-lg" 
                />
              </div>
              <p className="text-gray-600 mb-4">
                Want to strengthen your understanding and skills? Our tutoring covers:
              </p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• One-to-one and group sessions</li>
                <li>• Subject-specific support (Math, Science, Business, Humanities, Engineering, etc.)</li>
                <li>• Exam preparation and academic skills development</li>
              </ul>
              <Link
                to="/tutoring-services"
                className="inline-flex items-center text-primary font-semibold hover:text-blue-700 transition-colors group"
              >
                Learn More
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-3xl">🇺🇸</span>
                <h3 className="text-xl font-semibold text-gray-900">Online Exam Help US</h3>
              </div>
              <div className="mb-6 w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 space-y-2">
                <div className="text-3xl">🖼️</div>
                <p className="text-xs">Image Placeholder</p>
              </div>
              <p className="text-gray-600 mb-4">
                Preparing for a US exam? Our dedicated exam help service covers:
              </p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• Standardized tests: SAT, ACT, GRE, GMAT, LSAT &amp; more</li>
                <li>• Course-specific finals, midterms, and quizzes</li>
                <li>• Guaranteed pass support with personalized strategies</li>
              </ul>
              <Link
                to="/online-exam-help"
                className="inline-flex items-center text-primary font-semibold hover:text-blue-700 transition-colors group"
              >
                Learn More
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* How It Works */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20 relative overflow-hidden"
      >
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(https://i.ibb.co/kVDSHFVh/How-It-Works-BG.jpg)' }}
        />
        <div className="absolute inset-0 bg-primary/85" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">How It Works (3 Easy Steps)</h2>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Getting academic help has never been easier. Follow these simple steps to get started.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="text-center relative"
              >
                <div className="w-16 h-16 bg-accent text-black rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10">
                  {step.number}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-white/30 -z-10" />
                )}
                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-blue-100">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/contact"
                  className="bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors shadow-lg hover:shadow-xl"
                >
                  👉 Order Now
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/book-tutor"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors"
                >
                  👉 Book a Session
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Testimonials */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20 bg-muted"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">What Our Students Say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real feedback from students who have transformed their academic journey with our support.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic text-lg">"{testimonial.text}"</p>
                <p className="text-gray-900 font-semibold">– {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20 bg-primary text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-blue-600 to-blue-800" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 variants={itemVariants} className="text-3xl lg:text-4xl font-bold mb-4">
            🎯 Don't let academic stress hold you back!
          </motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            With Tutor Hub Online's assignment support and tutoring services, you'll get the expert support you need to meet deadlines, improve understanding, and achieve success.
          </motion.p>
          <motion.p variants={itemVariants} className="text-lg mb-8">
            📩 Contact us today and take the first step toward better grades!
          </motion.p>
          <motion.div variants={itemVariants} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/book-tutor"
              className="bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg shadow-xl hover:shadow-2xl"
            >
              👉 Get Started Now
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Homepage;